/**
 * a11y.ts — the shared modal-dialog keyboard contract: what is focusable, and how a dialog traps Tab.
 *
 * WHY THIS FILE EXISTS. The same focus trap had been inlined three times, with divergent behaviour —
 * `learning/LearningHub.tsx`'s `Overlay` (capture-phase listener, restores focus on close),
 * `tools/upload/UploadOverlay.tsx` (React `onKeyDown`, additionally filters `offsetParent === null`
 * so a hidden control is skipped), and the three `Profile.tsx` dialogs (Escape only — **no** Tab trap
 * at all). Adding a fourth copy for the guided tour would have made four. This is the intended shared
 * home: `useFocusTrap` below is the `LearningHub` version — the most complete of the three — plus
 * UploadOverlay's hidden-element filter, which is a strict improvement and changes nothing when no
 * control is hidden.
 *
 * The three existing call sites are deliberately NOT migrated here yet. They work, and rewriting the
 * focus behaviour of three shipped overlays is churn in files the guided-tour change does not
 * otherwise touch — a separate, reviewable slice. New dialogs should use this hook.
 *
 * PC-NFR01 (WCAG 2.1 AA) is what this serves: 2.1.2 No Keyboard Trap (Tab cycles WITHIN the dialog
 * and Escape always gets you out), and 2.4.3 Focus Order (focus enters the dialog on open and returns
 * to whatever opened it on close, so a keyboard user is never dropped at the top of the document).
 */
import { useEffect, useRef, useState, type RefObject } from 'react';

/**
 * Everything the browser will focus with Tab. Kept as ONE string so a dialog's "first"/"last"
 * focusable is computed the same way everywhere. `[tabindex="-1"]` is excluded deliberately: a
 * programmatically-focusable container (the dialog itself) must not become a Tab stop.
 */
export const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/** The focusable descendants of `root`, in DOM order, minus any that are currently not rendered. */
function focusablesWithin(root: HTMLElement | null): HTMLElement[] {
  if (!root) return [];
  // `offsetParent === null` catches a control inside a `display: none` branch — a collapsed panel,
  // a step that is not the current one. Tabbing to something invisible looks like a dead keypress.
  return Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(
    (el) => el.offsetParent !== null,
  );
}

/**
 * Trap keyboard focus inside a dialog for as long as it is mounted.
 *
 * On mount: remembers what was focused, then moves focus to the dialog's first focusable control
 * (or the dialog element itself, which is why the caller must give it `tabIndex={-1}`).
 * While mounted: Tab and Shift+Tab cycle within the dialog; Escape calls `onClose`.
 * On unmount: restores focus to whatever had it before the dialog opened.
 *
 * The listener is registered in the CAPTURE phase on `document`, so it sees the key before any
 * handler on the page behind, and Escape is `stopPropagation`'d so it closes THIS dialog only —
 * never this one and something underneath it in the same keypress.
 *
 * ── Why `onClose` is held in a ref rather than depended on ────────────────────────────────────
 * It used to be in the dependency array, with a doc line telling callers to pass a stable callback.
 * No caller did — the admin surfaces pass `() => setOpenPanel(null)`, a fresh closure every render —
 * so the effect tore down and rebuilt on EVERY re-render of the dialog, and a dialog re-renders as
 * soon as its data arrives. Each teardown ran the restore and each rebuild re-captured
 * "previously focused", so by the time the dialog really closed, the node being restored to was a
 * control INSIDE the dialog that was about to be removed. Focusing a detached node does nothing, so
 * focus fell to `<body>` and a keyboard user was dropped at the top of the document — exactly the
 * 2.4.3 Focus Order failure this hook exists to prevent. Traced in a real browser on 2026-09-01:
 * `focusin dialog → focusin Close → focusin dialog → focusin Close`, then no focusin at all on close.
 *
 * So: the effect now depends on `[ref]` alone and runs ONCE per mount, `onClose` is read through a
 * ref at keypress time, and the opener is captured once and restored only if it is still connected.
 *
 * @param ref     the dialog element (`role="dialog" aria-modal="true" tabIndex={-1}`).
 * @param onClose what Escape means. Any identity is fine; it is read through a ref, not depended on.
 */
export function useFocusTrap(ref: RefObject<HTMLElement | null>, onClose: () => void): void {
  // Latest `onClose` without making it a dependency. Kept current in its own effect rather than
  // assigned during render, which the react-hooks/refs rule rightly flags.
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  /**
   * THE OPENER, captured during the FIRST RENDER and never again.
   *
   * It cannot be read in the effect below. A dialog's own markup may move focus in a ref callback —
   * OverlayFrame does exactly that (`el.focus()` on the `role="dialog"` node,
   * tools/upload/design/OverlayFrame.tsx) — and React runs ref callbacks BEFORE effects. So by the
   * time an effect looks at `document.activeElement`, the answer is the dialog itself, and "restore
   * focus to the opener" silently becomes "restore focus to a node that is about to be detached".
   * Focusing a detached node does nothing, so focus lands on `<body>` and the keyboard user is
   * dropped at the top of the document — the 2.4.3 failure this hook exists to prevent. Traced in a
   * real browser on 2026-09-01 against the admin panels.
   *
   * `useState` with an initialiser is the capture point that runs before both: it is evaluated during
   * the first render, while the control that was clicked still holds focus.
   */
  const [opener] = useState<HTMLElement | null>(() => document.activeElement as HTMLElement | null);

  useEffect(() => {
    const initial = focusablesWithin(ref.current);
    (initial.length > 0 ? initial[0] : ref.current)?.focus();

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onCloseRef.current();
        return;
      }
      if (e.key !== 'Tab') return;
      const nodes = focusablesWithin(ref.current);
      if (nodes.length === 0) return;
      const first = nodes[0];
      const last = nodes[nodes.length - 1];
      if (!first || !last) return;
      const active = document.activeElement;
      if (e.shiftKey && active === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', onKeyDown, true);
    return () => {
      document.removeEventListener('keydown', onKeyDown, true);
      // Only restore to a node still in the document: focus() on a detached element is a silent
      // no-op that leaves focus on <body>, so guarding makes the failure visible rather than quiet.
      if (opener?.isConnected) opener.focus?.();
    };
    // `ref` is a stable ref object, so this runs once per mount. `onClose` is deliberately absent —
    // see the note above; depending on it made the effect thrash on every re-render.
  }, [ref, opener]);
}
